import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { definePage, defineResource } from '@restavo/crud';
import EmptyState from 'src/components/Base/EmptyState';
import MainMenuForm from 'src/components/Menu/MainMenuForm';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import { tenantMenu, tenantMenuCreate, tenantMenuDelete, tenantMenuUpdate } from 'src/services/apis/tenantRequests';
import { MenuEntity } from 'src/services/apis/types';
import { resolveIconUrl } from 'src/utils/images';
import { QUERIES } from 'src/utils/react-query';

const mainMenus = defineResource({
  key: QUERIES.MENU,
  // The hand-written hook sent no limit, so the server (alwaysPaginate, no configured take)
  // returned every row in one shot; the crud layer's paginated query needs a concrete page
  // size, and 1000 is the practical match — the same reach the pilot used for an
  // effectively-unbounded list.
  pageSize: 1000,
  api: {
    list: tenantMenu,
    create: tenantMenuCreate,
    update: tenantMenuUpdate,
    remove: tenantMenuDelete,
  },
  // The hand-written page filtered name/slug in memory over the fetched set.
  search: 'client',
  searchFields: ['name', 'slug'],
  filterable: [{ field: 'activated', labelKey: 'common.activated', type: 'boolean' }],
});

/**
 * The MainMenuSettings page config, as a hook because the preserved "total items" cell
 * renders translated copy that only a component may read (useTranslation).
 */
export const useMainMenuSettingsPage = () => {
  const { t } = useTranslation();
  const currentRestaurant = useCurrentRestaurant();

  // Memoized like the other feature configs: a fresh `page` object every render gives
  // CrudPage a new `presentation` identity, which re-runs its own memos (sortableKeys,
  // ordered columns) for a config that never changed.
  const page = useMemo(
    () =>
      definePage(mainMenus, {
        titleKey: 'titles.menuPage',
        descriptionKey: 'common.menu',
        createLabelKey: 'actions.createNewMenu',
        updateLabelKey: 'actions.updateMenu',
        deleteHeadingKey: 'messages.areYouSureToDeleteThisMenu',
        columns: [
          // The column a user edits by name must survive every prefs reconciliation.
          { key: 'name', headerKey: 'common.name', sortable: true, alwaysVisible: true },
          { key: 'slug', headerKey: 'common.slug' },
          { key: 'summary', headerKey: 'common.summary' },
          // The hand-written page hardcoded "0 items" here — the menu list has no item counts
          // to render — and a migration must not invent data, so the literal survives.
          { key: 'totalItems', headerKey: 'common.totalItems', render: () => `0 ${t('common.items')}` },
          // Same 32×32 photo the hand-written cell drew, guards included.
          {
            key: 'icon',
            headerKey: 'common.icon',
            render: (row: MenuEntity) => {
              const iconUrl = resolveIconUrl(row.photos?.[0]);
              return iconUrl ? <img src={iconUrl} width={32} height={32} alt="" /> : null;
            },
          },
          // The dead per-row Toggle (no onToggle handler, so it never changed anything) becomes
          // the same yes/no copy as static text.
          { key: 'activated', headerKey: 'common.activated', display: { kind: 'boolean' } },
        ],
        form: MainMenuForm,
        emptyState: <EmptyState />,
        toPayload: (values: any, params) => ({
          ...values,
          restaurantId: params.restaurantId,
          // Guarded like every sibling feature: an empty icon must post [], not [undefined] or
          // [''], or the tightened MenuCreateDto.photos rule answers 422.
          photos: values.icon ? [values.icon] : [],
          blurhashPhotos: [],
        }),
        useTitleVars: () => ({ restaurantName: currentRestaurant?.name }),
      }),
    [t, currentRestaurant?.name]
  );

  return { page };
};
